import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { ThemeIcon } from 'src/design/atoms/ThemeIcon';
import { Title } from 'src/design/atoms/Title';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { Checkbox } from 'src/design/atoms/Checkbox';
import {
  IconAlertCircleFilled,
  IconCircleCheckFilled,
} from '@tabler/icons-react';
import { type MRT_ColumnDef } from 'mantine-react-table';
import React from 'react';
import { MissingTimesheetUserRow } from 'src/api/FiretrolApiClient';
import { getCurrentWorkWeek, getWorkWeekRange } from 'src/utils/workWeek';
import { formatDate } from 'src/utils';
import { useMissingTimesheets } from 'src/api/queries/timesheets/useMissingTimesheets';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

const EMPTY_ROWS: MissingTimesheetUserRow[] = [];

export interface IMissingTimesheetsCardProps {
  officeId?: number;
  weekId?: number;
  year?: number;
}

export const MissingTimesheetsCard: React.FC<IMissingTimesheetsCardProps> = ({
  officeId,
  weekId,
  year,
}) => {
  const [showMyTeam, setShowMyTeam] = React.useState(false);
  const week = React.useMemo(
    () => (weekId && weekId > 0 ? weekId : getCurrentWorkWeek().weekNumber),
    [weekId],
  );
  // Selected week's own year - the card shows whichever week is selected, not "now".
  const selectedYear = React.useMemo(
    () => (year && year > 0 ? year : getCurrentWorkWeek().year),
    [year],
  );
  const weekRange = React.useMemo(() => {
    const { start: startDate, end: endDate } = getWorkWeekRange(
      week,
      selectedYear,
    );
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }, [week, selectedYear]);

  const {
    data: missingTimesheetUsers = EMPTY_ROWS,
    isLoading: loading,
    isError,
  } = useMissingTimesheets(officeId, {
    year: selectedYear,
    weekId: week,
    showMyTeam,
  });

  const columns = React.useMemo<MRT_ColumnDef<MissingTimesheetUserRow>[]>(
    () => [
      {
        id: 'status',
        header: '',
        size: 40,
        enableColumnFilter: false,
        enableSorting: false,
        enableColumnDragging: false,
        enableColumnActions: false,
        enableColumnOrdering: false,
        enableResizing: false,
        Cell: ({ row }) => {
          const hasNoHours = Number(row.original.totals.total) === 0;
          return (
            <Tooltip
              label={hasNoHours ? 'No hours submitted' : 'Has hours'}
              withArrow
            >
              <ThemeIcon
                color={hasNoHours ? 'red' : 'green'}
                variant="light"
                size="sm"
              >
                {hasNoHours ? (
                  <IconAlertCircleFilled size={16} />
                ) : (
                  <IconCircleCheckFilled size={16} />
                )}
              </ThemeIcon>
            </Tooltip>
          );
        },
      },
      { accessorKey: 'firstName', header: 'First', width: 100 },
      { accessorKey: 'lastName', header: 'Last', width: 100 },
      { accessorKey: 'employeeId', header: 'Employee ID' },
      { accessorKey: 'email', header: 'Email' },
      { accessorKey: 'totals.sun', header: 'Sun', width: 75 },
      { accessorKey: 'totals.mon', header: 'Mon', width: 75 },
      { accessorKey: 'totals.tue', header: 'Tue', width: 75 },
      { accessorKey: 'totals.wed', header: 'Wed', width: 75 },
      { accessorKey: 'totals.thu', header: 'Thu', width: 75 },
      { accessorKey: 'totals.fri', header: 'Fri', width: 75 },
      { accessorKey: 'totals.sat', header: 'Sat', width: 75 },
      { accessorKey: 'totals.total', header: 'Total', width: 75 },
    ],
    [],
  );

  const tableState = useTableState<MissingTimesheetUserRow>({
    identifier: `missing-timesheets-${officeId}-${week}`,
    columns,
    defaultSorting: [{ id: 'lastName', desc: false }],
    defaultColumnVisibility: {
      employeeId: false,
      email: false,
    },
  });

  const tableExports = useTableExports<MissingTimesheetUserRow>(
    `missing-timesheets-week-${week}`,
    (data) => {
      return data.map((item) => ({
        ['First']: item.firstName,
        ['Last']: item.lastName,
        ['Employee ID']: item.employeeId || '',
        ['Email']: item.email || '',
        ['Sun']: item.totals.sun || 0,
        ['Mon']: item.totals.mon || 0,
        ['Tue']: item.totals.tue || 0,
        ['Wed']: item.totals.wed || 0,
        ['Thu']: item.totals.thu || 0,
        ['Fri']: item.totals.fri || 0,
        ['Sat']: item.totals.sat || 0,
        ['Total']: item.totals.total || 0,
      }));
    },
  );

  const { table } = useTable<MissingTimesheetUserRow>({
    ...tableState,
    tableOptions: {
      enableRowSelection: false,
      enableRowActions: false,
      enableClickToCopy: false,
      columns,
      data: missingTimesheetUsers,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      enableStickyHeader: true,
      mantineTableContainerProps: { style: { maxHeight: '600px' } },
      renderTopToolbarCustomActions: () => (
        <Checkbox
          label="Show Only My Team"
          description="Only show users that are supervised by you or supervised by a user that is supervised by you"
          checked={showMyTeam}
          onChange={(e) => setShowMyTeam(e.currentTarget.checked)}
        />
      ),
      mantineTableBodyRowProps: ({ row }) => {
        if (row.id == 'undefined') {
          return {};
        }
        const isDanger = Number(row.original.totals?.total) === 0;
        return isDanger
          ? {
              style: {
                backgroundColor: 'var(--mantine-color-red-1)',
              },
            }
          : {};
      },
      mantineTableBodyCellProps: ({ cell }) => {
        if (cell.row.id == 'undefined') {
          return {};
        }
        if (Number(cell.row.original.totals?.total) === 0) {
          return;
        }

        const dayColumns = [
          'totals.mon',
          'totals.tue',
          'totals.wed',
          'totals.thu',
          'totals.fri',
        ];
        if (dayColumns.includes(cell.column.id)) {
          const value = Number(cell.getValue());
          if (value === 0) {
            return {
              style: {
                backgroundColor: 'var(--mantine-color-yellow-1)',
              },
            };
          }
        }
        return {};
      },
    },
    getRowId: (row) => String(row.id),
    totalCount: missingTimesheetUsers.length,
    isLoading: loading,
    isFetching: loading,
    isError,
    ...tableExports,
  });

  return (
    <Stack>
      <Title order={4}>
        Missing Time for Week {week} ({weekRange})
      </Title>
      <Text>
        Don't see someone? Please make sure they are assigned to the correct
        office. Only users who are assigned to the selected office are shown.
      </Text>
      {table}
    </Stack>
  );
};
